/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
import { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useLocation } from 'react-router-dom'

import type { SaasAuthMfaDeviceResponse } from '@modules/cloud-api/v1/types'
import { filterWeakMfaDevices, hasStrongMfaDevice } from '@modules/mfa-lib/devices'
import type { AuthContextData } from '@modules/auth/context'
import AuthContext from '@modules/auth/context'
import { useProfile } from '@modules/profile-lib/hooks'
import type { UserProfileFromApi } from '@modules/ui-types'
import {
  useGetSaasCurrentUserMfaDevicesQuery,
  useGetSaasCurrentUserMfaEnabled,
} from '@modules/cloud-lib/users/hooks/mfa'
import { useLocalStorage } from '@modules/utils/hooks/useLocalStorage'
import LocalStorageKey from '@modules/utils/localStorageKeys'

// eslint-disable-next-line import/no-restricted-paths
import { userSettingsUrl } from '@/lib/urlBuilder'

export function useIsMfaEnforced(): boolean | null {
  const [isMfaEnforced, setIsMfaEnforced] = useState<boolean | null>(null)

  const profile = useProfile()
  const mfaEnabled = useGetSaasCurrentUserMfaEnabled()
  const devices = useGetSaasCurrentUserMfaDevicesQuery().data?.mfa_devices

  const {
    authContext: { method: authMethod },
  } = useContext(AuthContext)

  useEffect(() => {
    if (isMfaEnforced === null) {
      const meetsRequirements = meetsMfaRequirements({
        profile,
        mfaEnabled,
        authMethod,
        devices,
      })

      if (meetsRequirements !== null) {
        setIsMfaEnforced(!meetsRequirements)
      }
    }
  }, [profile, mfaEnabled, isMfaEnforced, authMethod, devices])

  return isMfaEnforced
}

export function meetsMfaRequirements({
  profile,
  mfaEnabled,
  authMethod,
  devices,
}: {
  profile: UserProfileFromApi | undefined
  mfaEnabled: boolean | undefined
  authMethod: AuthContextData['method']
  devices: SaasAuthMfaDeviceResponse[] | undefined
}): boolean | null {
  // loading states
  if (!profile) {
    return null
  }

  if (!devices) {
    return null
  }

  if (mfaEnabled === undefined) {
    return null
  }

  // cases in which there is no need to enforce MFA
  if (!profile.mfa_enforced) {
    // mfa_enforced from profile API
    return true
  }

  if (authMethod !== 'username-and-password') {
    // only enforce if they logged in with traditional credentials instead of e.g. a third party IdP.
    return true
  }

  // cases in which we do need to enforce MFA
  if (!mfaEnabled) {
    // user has MFA disabled, so we need to enforce it, doesn't matter if they have strong devices or not
    return false
  }

  // comes last, if user has strong MFA device, we don't need to enforce MFA
  return hasStrongMfaDevice(devices)
}

export function useShouldShowMfaNotice(): {
  shouldShowMfaNotice: boolean
  onDismiss: () => void
} {
  const [shouldShowMfaNotice, setShouldShowMfaNotice] = useState<boolean>(false)
  const [dismissedTime, setDismissedTime] = useLocalStorage(
    LocalStorageKey.backupMFACalloutDismissed,
    null,
  )

  const { pathname } = useLocation()

  const isDismissedTimeExpired =
    !dismissedTime || moment.utc().isSameOrAfter(moment.utc(dismissedTime).add(1, 'week'))

  const devices = useGetSaasCurrentUserMfaDevicesQuery({ enabled: isDismissedTimeExpired }).data
    ?.mfa_devices

  const {
    authContext: { method: authMethod },
  } = useContext(AuthContext)

  const onDismiss = () => {
    setDismissedTime(moment.utc().format())
  }

  useEffect(() => {
    const meetsRequirements = meetsMfaBackupNoticeRequirements({
      pathname,
      isDismissedTimeExpired,
      authMethod,
      devices,
    })

    if (meetsRequirements !== null) {
      setShouldShowMfaNotice(meetsRequirements)
    }
  }, [pathname, authMethod, shouldShowMfaNotice, devices, isDismissedTimeExpired])

  return { shouldShowMfaNotice, onDismiss }
}

export function meetsMfaBackupNoticeRequirements({
  pathname,
  isDismissedTimeExpired,
  authMethod,
  devices,
}: {
  pathname: string
  isDismissedTimeExpired: boolean
  authMethod: AuthContextData['method']
  devices: SaasAuthMfaDeviceResponse[] | undefined
}): boolean | null {
  // loading states
  if (!pathname) {
    return null
  }

  if (!devices) {
    return null
  }

  // MFA can be only managed if the authentication method is username and password
  const isMfaManageAllowed = authMethod === 'username-and-password'

  // This callout directs to the user profile page, so it should not be showing if already on that page
  const isNotUserProfileUrl = !pathname.startsWith(userSettingsUrl())

  const hasOnlyOneMfa = filterWeakMfaDevices({ devices }).length === 1

  return isMfaManageAllowed && isNotUserProfileUrl && hasOnlyOneMfa && isDismissedTimeExpired
}
